import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { Accordion, Stack, IconButton } from "@mui/material";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Typography from "@mui/material/Typography";
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";

import NotificationList from "./notificationList"; // adjust path to where your component lives

import type { RootState } from "../../../store/reducers/reducer"; // adjust path if needed

interface PersonsInfo {
  company_email: string | null;
  company_id: string | null;
  company_name: string | null;
  modified_dt: string | null;
  person_email: string | null;
  person_id: string | null;
  person_name: string | null;
  recipient_id: string | null;
  type: string | null;
}

interface VoyageGroup {
  voyageId: string;
  voyageName: string;
  vesselName: string;
  persons: PersonsInfo[];
}

type NotificationData = PersonsInfo[] | VoyageGroup[] | null;

interface NotificationAccordionProps {
  notifications?: NotificationData | null;
  defaultExpanded?: boolean;
  useNumbering?: boolean;
}

const AccordionHeader = ({
  vesselName,
  isExpanded,
}: {
  vesselName: string;
  isExpanded: boolean;
}) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    sx={{
      cursor: "pointer",
      bgcolor: "action.hover",
      height: 35,
      px: 1,
      py: 1,
      borderRadius: 1,
      width: "100%",
    }}
  >
    <Typography
      variant="subtitle2"
      overflow="hidden"
      textOverflow="ellipsis"
      whiteSpace="nowrap"
    >
      {vesselName}
    </Typography>

    <IconButton size="small" aria-label={isExpanded ? "collapse" : "expand"}>
      {isExpanded ? (
        <KeyboardArrowUp fontSize="small" />
      ) : (
        <KeyboardArrowDown fontSize="small" />
      )}
    </IconButton>
  </Stack>
);

const NotificationAccordion: React.FC<NotificationAccordionProps> = ({
  notifications,
  defaultExpanded = false,
  useNumbering = false,
}) => {
  const reduxState = useSelector((state: RootState) => state as any);

  const inferredNotifications = useMemo(() => {
    if (notifications) return notifications;

    const maybe =
      reduxState?.notificationList?.data ||
      reduxState?.notifications?.list ||
      reduxState?.notifications ||
      reduxState?.notificationList ||
      reduxState?.notification?.list ||
      null;

    if (maybe && !Array.isArray(maybe)) {
      if (Array.isArray(maybe.items)) return maybe.items;
      if (Array.isArray(maybe.data)) return maybe.data;
    }

    return Array.isArray(maybe) ? maybe : null;
  }, [notifications, reduxState]);

  const [expanded, setExpanded] = useState<boolean>(defaultExpanded);

  const handleChange = (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded);
  };

  // Check if notifications is voyage-wise grouped or flat
  const isVoyageWiseGrouped = useMemo(() => {
    return (
      Array.isArray(inferredNotifications) &&
      inferredNotifications.length > 0 &&
      'voyageId' in inferredNotifications[0]
    );
  }, [inferredNotifications]);

  // Render voyage-wise accordion
  if (isVoyageWiseGrouped) {
    return (
      <Stack spacing={1}>
        {(inferredNotifications as VoyageGroup[]).map((voyageGroup) => (
          <Accordion key={voyageGroup.voyageId} defaultExpanded={false}>
            <AccordionSummary
              aria-controls={`voyage-panel-${voyageGroup.voyageId}`}
              id={`voyage-header-${voyageGroup.voyageId}`}
              expandIcon={undefined}
              sx={{ px: 0 }}
            >
              <AccordionHeader
                vesselName={`${voyageGroup.vesselName} - ${voyageGroup.voyageName} `}
                isExpanded={false}
              />
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0 }}>
              <NotificationList
                data={voyageGroup.persons}
                mainIcon="•"
                useNumbering={useNumbering}
                showListBorder
                containerSx={{ width: "100%" }}
              />
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>
    );
  }

  // Render flat notification list
  return (
    <Accordion
      expanded={expanded}
      onChange={handleChange}
      defaultExpanded={defaultExpanded}
    >
      <AccordionSummary
        aria-controls="notification-panel-content"
        id="notification-panel-header"
        // Remove default expand icon; our header includes an icon
        expandIcon={undefined}
        sx={{ px: 0 }}
      >
        {/* Use Voyages as header text */}
        <AccordionHeader vesselName="Notification List" isExpanded={expanded} />
      </AccordionSummary>

      <AccordionDetails sx={{ px: 0 }}>
        {expanded ? (
          <NotificationList
            data={inferredNotifications}
            mainIcon="•"
            useNumbering={useNumbering}
            showListBorder
            containerSx={{ width: "100%" }}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            Open to view notifications
          </Typography>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default NotificationAccordion;
